<script setup>
import { ref, reactive, computed, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'
import { ST } from '@/constants/ST'

const emit = defineEmits(['adicionar-item', 'editar-item', 'show-notification'])

const authStore = useAuthStore()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao()

const lista = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const listas = reactive({ status: [] })

const filtros = reactive({
  busca: '',
  lstStatus: ''
})

const mostrarAuditoria = ref(false)
const auditoriaSelecionada = reactive({ id: null, nome: '' })

const temPermissaoCompleta = computed(() => authStore.pode('CONDICOES_PAGAMENTO_COMPLETO'))
const temPermissaoVisualizacao = computed(() => authStore.pode('CONDICOES_PAGAMENTO_VISUALIZACAO'))
const podeVerAcoes = computed(() => temPermissaoCompleta.value || temPermissaoVisualizacao.value)

const colunas = computed(() => {
  const cols = [
    { field: 'idCondicaoPagamento', label: 'ID', width: 80, sortable: true, slotName: 'id' },
    { field: 'descricao', label: 'Descrição', sortable: true, class: 'fw-medium text-body-emphasis' },
    { field: 'quantidadeParcelas', label: 'Parcelas', align: 'center', sortable: true, slotName: 'parcelas' },
    { field: 'diasIntervalo', label: 'Regra (Dias)', align: 'center', sortable: true, slotName: 'regra' },
    { field: 'diaFixoVencimento', label: 'Venc. Fixo', align: 'center', sortable: true, slotName: 'vencimentoFixo' },
    { field: 'entrada', label: 'Entrada (D+0)', align: 'center', sortable: true, slotName: 'entrada' },
    { field: 'lstStatus', label: 'Status', align: 'center', sortable: true, slotName: 'status' }
  ]
  if (podeVerAcoes.value) {
    cols.push({ field: 'acoes', label: 'Ações', width: 120, align: 'right', slotName: 'acoes', resizable: false, sortable: false })
  }
  return cols
})

const getStatusTexto = (idStatus) => {
  if (!idStatus) return '-'
  const obj = listas.status.find(s => s.idListaSistemaItem === idStatus || s.idListaClienteItem === idStatus)
  return obj ? obj.textoLista : idStatus
}

const sortResolver = (field, order, data) => {
  if (!field.startsWith('lst')) return null

  const resolvers = {
    lstStatus: (row) => getStatusTexto(row.lstStatus)
  }

  const resolver = resolvers[field]
  if (!resolver) return null

  return [...data].sort((a, b) => {
    const cmp = resolver(a).localeCompare(resolver(b), 'pt-BR')
    return order === 'asc' ? cmp : -cmp
  })
}

const handleSortChange = () => { buscarDados(1) }

watch(() => authStore.empresaAtiva, (novaEmpresa) => {
  if (novaEmpresa) buscarDados(1)
  else { lista.value = []; paginacao.value.TotalCount = 0 }
})

const carregarListas = async () => {
  try {
    const res = await api.get('/ListasSistema/lookup/ST')
    listas.status = res.data
  } catch (e) { console.warn('Erro ao carregar listas:', e) }
}

const buscarDados = async (pageNumber = 1) => {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página.'
    lista.value = []
    return
  }

  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)
    if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
    if (filtros.busca) params.append('busca', filtros.busca)
    if (filtros.lstStatus) params.append('lstStatus', filtros.lstStatus)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)

    const response = await api.get(`/CondicoesPagamento?${params.toString()}`)
    lista.value = response.data
    atualizarPaginacao(response.headers)
  } catch (error) {
    console.error('Erro ao buscar condições de pagamento:', error)
    erro.value = 'Não foi possível carregar os dados.'
    lista.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => { buscarDados(1) }
const limparFiltros = () => { filtros.busca = ''; filtros.lstStatus = ''; buscarDados(1) }

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) buscarDados(page)
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const editarRegistro = (item) => { emit('editar-item', item) }

const abrirAuditoria = (item) => {
  auditoriaSelecionada.id = item.idCondicaoPagamento
  auditoriaSelecionada.nome = item.descricao
  mostrarAuditoria.value = true
}

onMounted(async () => {
  await carregarListas()
  if (authStore.empresaAtiva || !authStore.isAdminInterno) buscarDados(1)
})

onActivated(() => { buscarDados(paginacao.value.CurrentPage || 1) })

defineExpose({ buscarDados })
</script>

<template>
  <PaginaAdministrar
    titulo="Condições de Pagamento"
    subtitulo="Gestão de regras de parcelamento, prazos e vencimentos"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    :mostrar-botao-adicionar="temPermissaoCompleta"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="emit('adicionar-item')"
    @atualizar="aplicarFiltros"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3 align-items-end">
        <div class="col-md-5">
          <label class="form-label">Descrição</label>
          <input type="text" class="form-control" v-model="filtros.busca" placeholder="Buscar..." @keydown.enter.prevent="aplicarFiltros">
        </div>
        <div class="col-md-4">
          <label class="form-label">Status</label>
          <select class="form-select" v-model="filtros.lstStatus" @change="aplicarFiltros">
            <option value="">Todos</option>
            <option v-for="s in listas.status" :key="s.idListaSistemaItem" :value="s.idListaSistemaItem">{{ s.textoLista }}</option>
          </select>
        </div>
        <div class="col-auto ms-auto">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5"><div class="spinner-border text-primary"></div></div>
    <div v-else-if="erro" class="alert alert-danger m-3 border-0 d-flex align-items-center">
      <i class="bi bi-exclamation-triangle-fill fs-4 me-3"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="lista"
      :loading="loading"
      empty-message="Nenhuma condição de pagamento encontrada."
      row-key="idCondicaoPagamento"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="editarRegistro($event.row)"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idCondicaoPagamento }}</span>
      </template>
      <template #parcelas="{ row }">
        {{ row.quantidadeParcelas }}x
      </template>

      <template #regra="{ row }">
        <span class="badge bg-body-secondary text-body border border-secondary-subtle">
          A cada {{ row.diasIntervalo }} dias
        </span>
      </template>

      <template #vencimentoFixo="{ row }">
        <span v-if="row.diaFixoVencimento" class="fw-bold">Dia {{ row.diaFixoVencimento }}</span>
        <span v-else class="text-muted">-</span>
      </template>

      <template #entrada="{ row }">
        <i v-if="row.entrada" class="bi bi-check-circle-fill text-success fs-5" title="Com Entrada"></i>
        <i v-else class="bi bi-x-circle-fill text-danger opacity-50 fs-5" title="Sem Entrada"></i>
      </template>

      <template #status="{ row }">
        <span class="badge" :class="row.lstStatus === ST.Ativo ? 'badge-ghost-success' : 'badge-ghost-danger'">
          {{ row.nomeStatus }}
        </span>
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1 acoes-wrapper" v-if="podeVerAcoes">
          <button class="btn-icon text-secondary" @click="abrirAuditoria(row)" title="Ver Histórico">
            <i class="bi bi-clock-history"></i>
          </button>
          <button class="btn-icon" @click="editarRegistro(row)" :title="temPermissaoCompleta ? 'Editar' : 'Visualizar'">
            <i :class="temPermissaoCompleta ? 'bi bi-pencil-fill text-primary' : 'bi bi-eye-fill text-secondary'"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="CondicaoPagamento"
    :chave-primaria="auditoriaSelecionada.id"
    :titulo-registro="auditoriaSelecionada.nome"
    contexto="tenant"
    @fechar="mostrarAuditoria = false"
  />
</template>

<style scoped>
</style>
